import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type Toast = { id: number; kind: 'ok' | 'error' | 'info'; text: string };
const Ctx = createContext<(kind: Toast['kind'], text: string) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x.slice(-4), { id, kind, text }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), kind === 'error' ? 8000 : 4000);
  }, []);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div aria-live="polite" className="fixed bottom-20 md:bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
        {items.map((t) => (
          <div key={t.id} role="status" className={`rounded-lg px-4 py-3 shadow-lg text-sm text-white ${t.kind === 'error' ? 'bg-red-700' : t.kind === 'ok' ? 'bg-brand-700' : 'bg-slate-800'}`}>
            {t.kind === 'error' ? '⚠ ' : t.kind === 'ok' ? '✓ ' : ''}
            {t.text}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);

export function errText(e: unknown) {
  const err = e as { message?: string; details?: { fields?: Record<string, string> } };
  const fields = err.details?.fields ? ` (${Object.entries(err.details.fields).map(([k, v]) => `${k}: ${v}`).join(', ')})` : '';
  return `${err.message ?? 'Something went wrong'}${fields}`;
}
